import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { t } from "../i18n";

function getAuthHeaders() {
  const token = localStorage.getItem("fm_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function votePlaylist(playlistId, vote) {
  const res = await fetch(`/api/playlists/${playlistId}/vote`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ vote }),
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) return { needLogin: true };
    throw new Error("Vote failed");
  }
  return res.json();
}

const PAGE_SIZE = 20;

function VoteButtons({ pl, onVote, voting }) {
  const voteAction = async (voteType) => {
    const isSame = pl.user_vote === voteType;
    onVote(pl.id, isSame ? "remove" : voteType);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => { e.stopPropagation(); voteAction("up"); }}
        disabled={voting[pl.id]}
        className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition ${
          pl.user_vote === "up"
            ? "bg-indigo-100 text-indigo-600"
            : "text-gray-400 hover:text-indigo-500 hover:bg-indigo-50"
        } disabled:opacity-50`}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill={pl.user_vote === "up" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
        {pl.upvote_count || 0}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); voteAction("down"); }}
        disabled={voting[pl.id]}
        className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition ${
          pl.user_vote === "down"
            ? "bg-red-50 text-red-500"
            : "text-gray-400 hover:text-red-400 hover:bg-red-50"
        } disabled:opacity-50`}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill={pl.user_vote === "down" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5">
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
        {pl.downvote_count || 0}
      </button>
    </div>
  );
}

export default function PlaylistsPage({ lang }) {
  const navigate = useNavigate();

  // My playlists state
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [myLoading, setMyLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [deleting, setDeleting] = useState(null);

  // All playlists state
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [allLoading, setAllLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalAll, setTotalAll] = useState(0);

  // Vote state
  const [voting, setVoting] = useState({});
  const [token, setToken] = useState(localStorage.getItem("fm_token"));

  // Listen for auth changes
  useEffect(() => {
    const check = () => setToken(localStorage.getItem("fm_token"));
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);

  // Fetch my playlists
  const fetchMyPlaylists = useCallback(async () => {
    if (!token) { setMyLoading(false); setMyPlaylists([]); return; }
    setMyLoading(true);
    try {
      const res = await fetch("/api/playlists/", {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setMyPlaylists(Array.isArray(data) ? data : data.playlists || []);
    } catch { setMyPlaylists([]); }
    finally { setMyLoading(false); }
  }, [token]);

  // Fetch all public playlists with pagination
  const fetchAllPlaylists = useCallback(async (p = 0) => {
    setAllLoading(true);
    try {
      const res = await fetch(`/api/playlists/public/all?limit=${PAGE_SIZE}&offset=${p * PAGE_SIZE}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setAllPlaylists(data.playlists || []);
      setTotalAll(data.total || 0);
    } catch { setAllPlaylists([]); }
    finally { setAllLoading(false); }
  }, []);

  useEffect(() => { fetchMyPlaylists(); }, [fetchMyPlaylists]);
  useEffect(() => { fetchAllPlaylists(page); }, [page, fetchAllPlaylists]);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch("/api/playlists/", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() }),
      });
      if (!res.ok) throw new Error("Create failed");
      const created = await res.json();
      setMyPlaylists((prev) => [...prev, created]);
      setShowCreate(false);
      setNewName("");
      setNewDesc("");
    } catch (e) { console.error("Create error:", e); }
    finally { setCreating(false); }
  }

  async function handleDelete(playlistId, e) {
    e.stopPropagation();
    if (!confirm(t(lang, "deletePlaylistConfirm"))) return;
    setDeleting(playlistId);
    try {
      const res = await fetch(`/api/playlists/${playlistId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Delete failed");
      setMyPlaylists((prev) => prev.filter((p) => p.id !== playlistId));
    } catch (e) { console.error("Delete error:", e); }
    finally { setDeleting(null); }
  }

  // Optimistic vote
  function handleVote(plId, voteType) {
    const updateList = (list) =>
      list.map((p) => {
        if (p.id !== plId) return p;
        const isSame = p.user_vote === voteType;
        const newVote = isSame ? "remove" : voteType;
        let up = p.upvote_count;
        let down = p.downvote_count;
        if (p.user_vote === "up") up = Math.max(0, up - 1);
        if (p.user_vote === "down") down = Math.max(0, down - 1);
        if (newVote === "up") up++;
        if (newVote === "down") down++;
        return { ...p, upvote_count: up, downvote_count: down, user_vote: newVote, net_score: up - down };
      });

    setMyPlaylists((prev) => updateList(prev));
    setAllPlaylists((prev) => updateList(prev));
    setVoting((v) => ({ ...v, [plId]: true }));

    const pl = myPlaylists.find((p) => p.id === plId) || allPlaylists.find((p) => p.id === plId);
    const prevVote = pl?.user_vote;
    const isSame = prevVote === voteType;
    const newVote = isSame ? "remove" : voteType;

    votePlaylist(plId, newVote).then((result) => {
      if (result.needLogin) {
        // Revert by re-fetching
        fetchAllPlaylists(page);
        fetchMyPlaylists();
      } else {
        // Sync from server
        const sync = (list) =>
          list.map((p) => {
            if (p.id !== plId) return p;
            return { ...p, upvote_count: result.upvotes ?? p.upvote_count, downvote_count: result.downvotes ?? p.downvote_count, user_vote: result.vote ?? newVote };
          });
        setMyPlaylists((prev) => sync(prev));
        setAllPlaylists((prev) => sync(prev));
      }
    }).catch(() => {
      fetchAllPlaylists(page);
      fetchMyPlaylists();
    }).finally(() => {
      setVoting((v) => ({ ...v, [plId]: false }));
    });
  }

  const totalPages = Math.max(1, Math.ceil(totalAll / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${lang === 'zh' ? '播放列表' : 'Playlists'} — FreeMusic.Top`}</title>
        <meta name="description" content={lang === 'zh' ? '浏览和发现 AI 生成的场景音乐播放列表' : 'Browse and discover AI-generated scene music playlists on FreeMusic.Top'} />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">{t(lang, "playlistManage")}</h1>
          </div>
          {token && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition shadow-sm"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t(lang, "createPlaylist")}
            </button>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {/* ── My Playlists ── */}
        {token && (
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              📋 {t(lang, "myPlaylists")}
            </h2>
            {myLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
              </div>
            ) : myPlaylists.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">{t(lang, "emptyPlaylist")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myPlaylists.map((pl) => (
                  <div
                    key={pl.id}
                    onClick={() => navigate(`/playlist/${pl.id}`)}
                    className="group bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100/50 p-4 cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      {pl.cover_url ? (
                        <img src={pl.cover_url} alt={pl.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0 shadow-sm" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                            <path d="M9 18V5l12-2v13" />
                            <circle cx="6" cy="18" r="3" />
                            <circle cx="18" cy="16" r="3" />
                          </svg>
                        </div>
                      )}
                      <button
                        onClick={(e) => handleDelete(pl.id, e)}
                        disabled={deleting === pl.id}
                        className={`w-7 h-7 flex items-center justify-center rounded text-gray-300 hover:text-red-400 hover:bg-red-50 transition opacity-0 group-hover:opacity-100 ${deleting === pl.id ? "opacity-50 pointer-events-none" : ""}`}
                        title={t(lang, "deleteConfirm")}
                      >
                        {deleting === pl.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5 truncate">{pl.name}</h3>
                    {pl.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{pl.description}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-400">
                        {pl.track_count || 0} {t(lang, "tracksInPlaylist")}
                      </div>
                      <VoteButtons pl={pl} onVote={handleVote} voting={voting} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── All Public Playlists ── */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            🌐 {t(lang, "allPlaylists")}
          </h2>
          {allLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : allPlaylists.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">{t(lang, "playlistEmpty")}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {allPlaylists.map((pl) => (
                  <div
                    key={pl.id}
                    onClick={() => navigate(`/playlist/${pl.id}`)}
                    className="group bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100/50 p-4 cursor-pointer transition-all"
                  >
                    <div className="flex items-start mb-2">
                      {pl.cover_url ? (
                        <img src={pl.cover_url} alt={pl.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0 shadow-sm mr-3" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-sm mr-3">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                            <path d="M9 18V5l12-2v13" />
                            <circle cx="6" cy="18" r="3" />
                            <circle cx="18" cy="16" r="3" />
                          </svg>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{pl.name}</h3>
                        <p className="text-xs text-gray-400 truncate">{pl.creator_name || "—"}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-400">
                        {pl.track_count || 0} {t(lang, "tracksInPlaylist")}
                      </div>
                      <VoteButtons pl={pl} onVote={handleVote} voting={voting} />
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    ← {t(lang, "previous")}
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i).map((i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      className={`w-8 h-8 text-sm rounded-lg transition ${
                        page === i
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  {totalPages > 10 && <span className="text-gray-400 text-sm">...</span>}
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {t(lang, "next")} →
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Create Playlist Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">{t(lang, "createPlaylist")}</h2>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t(lang, "playlistName")}</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t(lang, "playlistName")}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t(lang, "playlistDesc")}</label>
                <textarea
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder={t(lang, "playlistDesc")}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition">
                {t(lang, "cancel")}
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition ${!newName.trim() || creating ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"}`}
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t(lang, "confirm")}
                  </span>
                ) : t(lang, "confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
